import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';

function pvParsePositive(value, fallback) {
  const n = parseInt(value, 10);
  return n >= 1 ? n : fallback;
}

// Clamp + snap-to-step using min / max / step from the input attributes.
function pvClampQty(input, candidate) {
  const min = pvParsePositive(input.min, 1);
  const step = pvParsePositive(input.step, 1);
  const max = parseInt(input.max, 10);
  let n = parseInt(candidate, 10);
  if (isNaN(n) || n < min) n = min;
  if (max >= min && n > max) n = max;
  if (step > 1) {
    const offset = (n - min) % step;
    if (offset !== 0) n = n - offset;
  }
  input.value = n;
  return n;
}

// Prevent qty input interactions from bubbling up to card-level navigation.
function pvStopOnQtyInput(e) {
  if (e.target?.matches?.('input[class$="__qty-value"]')) e.stopPropagation();
}

/**
 * Sale products section with filter tabs and carousel.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} filterTabs - Filter tab buttons.
 * @property {HTMLElement} productsTrack - The scrollable products container.
 * @property {HTMLElement} prevBtn - Previous carousel button.
 * @property {HTMLElement} nextBtn - Next carousel button.
 * @property {HTMLElement} progressBar - Progress indicator bar.
 *
 * @extends Component<Refs>
 */
class PvSaleProducts extends Component {
  requiredRefs = ['productsTrack'];

  #prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  #onTrackScroll = () => {
    this.#updateProgress();
    this.#updateNavState();
  };

  connectedCallback() {
    super.connectedCallback();

    const { filterTabs, productsTrack, prevBtn, nextBtn } = this.refs;

    // Filter tabs
    if (filterTabs) {
      filterTabs.forEach((tab) => {
        tab.addEventListener('click', (e) => this.#handleFilterClick(e));
      });
    }

    // Carousel navigation
    if (prevBtn) prevBtn.addEventListener('click', () => this.#scroll(-1));
    if (nextBtn) nextBtn.addEventListener('click', () => this.#scroll(1));

    // Track scroll for progress bar + nav state
    if (productsTrack) {
      productsTrack.addEventListener('scroll', this.#onTrackScroll, { passive: true });
    }

    // Quantity selectors — +/- buttons and direct input editing.
    // Min / step / max come from the input attributes (server-rendered per
    // variant via pv-qty-stepper.liquid) so the rules match collection cards.
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-qty-action]');
      if (!btn) return;

      const wrapper = btn.closest('.pv-sale__qty');
      const input = wrapper?.querySelector('.pv-sale__qty-value');
      if (!input) return;

      const step = pvParsePositive(input.step, 1);
      const current = parseInt(input.value, 10) || pvParsePositive(input.min, 1);
      const next = btn.dataset.qtyAction === 'minus' ? current - step : current + step;
      pvClampQty(input, next);
    });

    // Stop input pointer/keyboard events from bubbling to the card link.
    this.addEventListener('mousedown', pvStopOnQtyInput);
    this.addEventListener('pointerdown', pvStopOnQtyInput);
    this.addEventListener('click', pvStopOnQtyInput);
    this.addEventListener('focusin', pvStopOnQtyInput);
    this.addEventListener('keydown', (e) => {
      if (!e.target.classList?.contains('pv-sale__qty-value')) return;
      e.stopPropagation();
      if (e.key === 'Enter') {
        e.preventDefault();
        e.target.blur();
      }
    });
    this.addEventListener('input', (e) => {
      if (!e.target.classList?.contains('pv-sale__qty-value')) return;
      e.stopPropagation();
      const max = parseInt(e.target.max, 10);
      const raw = parseInt(e.target.value, 10);
      if (!isNaN(raw) && max && raw > max) e.target.value = max;
    });
    this.addEventListener('change', (e) => {
      if (!e.target.classList?.contains('pv-sale__qty-value')) return;
      pvClampQty(e.target, parseInt(e.target.value, 10));
    });
    this.addEventListener('blur', (e) => {
      if (!e.target.classList?.contains('pv-sale__qty-value')) return;
      pvClampQty(e.target, parseInt(e.target.value, 10));
    }, true);

    // Add to cart
    this.addEventListener('click', (e) => {
      const cartBtn = e.target.closest('[data-add-to-cart]');
      if (!cartBtn) return;
      e.preventDefault();

      const variantId = cartBtn.dataset.addToCart;
      const card = cartBtn.closest('.pv-sale__card');
      const qtyEl = card?.querySelector('.pv-sale__qty-value');
      const quantity = parseInt(qtyEl?.value, 10) || 1;

      if (!cartBtn._originalHtml) {
        cartBtn._originalHtml = cartBtn.innerHTML;
      }

      const item = { id: parseInt(variantId, 10), quantity };
      if (cartBtn.dataset.pvMinMaxRestriction === 'true') {
        item.properties = { _min_or_max_restriction: 'true' };
      }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [item] }),
      })
        .then((res) => res.json())
        .then(() => {
          cartBtn.textContent = 'Added!';
          setTimeout(() => {
            cartBtn.innerHTML = cartBtn._originalHtml;
          }, 1500);

          return fetch('/cart.js').then((r) => r.json());
        })
        .then((cart) => {
          this.dispatchEvent(
            new CartAddEvent(cart, this.dataset.sectionId, {
              source: 'pv-sale-products',
              itemCount: cart.item_count,
            })
          );
        })
        .catch(() => {
          cartBtn.textContent = 'Error';
          setTimeout(() => {
            cartBtn.innerHTML = cartBtn._originalHtml;
          }, 1500);
        });
    });

    this.#updateProgress();
    this.#updateNavState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const { productsTrack } = this.refs;
    if (productsTrack) {
      productsTrack.removeEventListener('scroll', this.#onTrackScroll);
    }
  }

  #handleFilterClick(e) {
    const tab = e.currentTarget;
    const { filterTabs, productsTrack } = this.refs;

    filterTabs.forEach((t) => {
      t.classList.remove('pv-sale__filter--active');
      t.setAttribute('aria-selected', 'false');
    });
    tab.classList.add('pv-sale__filter--active');
    tab.setAttribute('aria-selected', 'true');

    const collectionHandle = tab.dataset.collection;
    const sectionId = this.dataset.sectionId;

    if (productsTrack) {
      productsTrack.classList.add('pv-sale__track--loading');
    }

    fetch(`/collections/${collectionHandle}?section_id=${sectionId}`)
      .then((res) => res.text())
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newTrack = doc.querySelector('.pv-sale__track');
        if (newTrack && productsTrack) {
          productsTrack.innerHTML = newTrack.innerHTML;
          productsTrack.scrollLeft = 0;

          // Stagger in product cards from left to right
          const cards = productsTrack.querySelectorAll('.pv-sale__card');
          cards.forEach((card, index) => {
            card.style.setProperty('--pv-sale-card-delay', `${index * 60}ms`);
            card.classList.add('pv-sale__card--enter');
          });

          this.#updateProgress();
          this.#updateNavState();

          // Fade new content back in
          requestAnimationFrame(() => {
            productsTrack.classList.remove('pv-sale__track--loading');
          });
        } else if (productsTrack) {
          productsTrack.classList.remove('pv-sale__track--loading');
        }
      })
      .catch(() => {
        if (productsTrack) {
          productsTrack.classList.remove('pv-sale__track--loading');
        }
      });
  }

  #scroll(direction) {
    const { productsTrack } = this.refs;
    if (!productsTrack) return;

    const cardWidth = productsTrack.querySelector('.pv-sale__card')?.offsetWidth || 340;
    const gap = parseFloat(getComputedStyle(productsTrack).gap) || 0;

    productsTrack.scrollBy({
      left: direction * (cardWidth + gap),
      behavior: this.#prefersReduced ? 'auto' : 'smooth',
    });

    setTimeout(() => this.#updateNavState(), 350);
  }

  #updateProgress() {
    const { productsTrack, progressBar } = this.refs;
    if (!productsTrack || !progressBar) return;

    const { scrollLeft, scrollWidth, clientWidth } = productsTrack;
    const maxScroll = scrollWidth - clientWidth;
    const ratio = maxScroll > 0 ? scrollLeft / maxScroll : 0;
    const barWidth = maxScroll > 0 ? (clientWidth / scrollWidth) * 100 : 100;

    progressBar.style.width = `${barWidth}%`;
    progressBar.style.marginLeft = `${ratio * (100 - barWidth)}%`;
  }

  #updateNavState() {
    const { productsTrack, prevBtn, nextBtn } = this.refs;
    if (!productsTrack) return;

    const { scrollLeft, scrollWidth, clientWidth } = productsTrack;
    const maxScroll = scrollWidth - clientWidth;

    prevBtn?.setAttribute('aria-disabled', scrollLeft <= 0 ? 'true' : 'false');
    nextBtn?.setAttribute('aria-disabled', scrollLeft >= maxScroll - 1 ? 'true' : 'false');
  }
}

if (!customElements.get('pv-sale-products')) {
  customElements.define('pv-sale-products', PvSaleProducts);
}
